import { Box, Fade } from '@mui/material'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { grey } from '../../colors'
import { HighlightBox } from './components/HighlightBox'
import { throttle } from 'lodash'
import { GuidedTourStepKeys, GuidedTourSteps } from './constants'

interface GuidedTourFeatureProps {
  isGuidedTourOpen: boolean
  setIsGuidedTourOpen: (isOpen: boolean) => void
  headerRef: React.RefObject<HTMLDivElement>
  syncButtonRef: React.RefObject<HTMLDivElement>
  emailSettingsButtonRef: React.RefObject<HTMLDivElement>
  emailEdit: boolean
  coursesRef: React.RefObject<HTMLDivElement>
}

export const GuidedTourFeature: React.FC<GuidedTourFeatureProps> = ({
  headerRef,
  isGuidedTourOpen,
  setIsGuidedTourOpen,
  syncButtonRef,
  emailSettingsButtonRef,
  emailEdit = false,
  coursesRef
}): JSX.Element | null => {
  const [currStep, setCurrStep] = useState<number>(GuidedTourStepKeys.WELCOME_STEP)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const handleNextClicked = (): void => {
    const nextStepIndex = currStep + 1
    if (nextStepIndex < steps.length) {
      setCurrStep(nextStepIndex)
    } else handleClose()
  }

  const handleClose = (): void => {
    setIsGuidedTourOpen(false)
    setCurrStep(GuidedTourStepKeys.WELCOME_STEP)
    setRect(null)
  }

  const handlePreviousClicked = (): void => {
    const previousStepIndex = currStep - 1
    if (previousStepIndex >= 0) {
      setCurrStep(previousStepIndex)
    } else handleClose()
  }

  const steps = GuidedTourSteps({ headerRef, syncButtonRef, emailSettingsButtonRef, coursesRef, emailEdit })

  // update the rect based off the current step element
  const step = steps[currStep]
  useEffect(() => {
    const currRef = step?.ref?.current
    if (!currRef) {
      setRect(null)
      return
    }
    const updateRectSize = (): void => {
      const newRect = currRef.getBoundingClientRect()
      setRect(prev => {
        const hasDOMRectChanged = !prev ||
          (prev.top !== newRect.top) ||
          (prev.left !== newRect.left) ||
          (prev.width !== newRect.width) ||
          (prev.height !== newRect.height)

        if (hasDOMRectChanged) return newRect
        return prev
      })
    }
    const throttledUpdate = throttle(updateRectSize, 100)
    updateRectSize()
    const resizeObserver = new ResizeObserver(throttledUpdate)
    resizeObserver.observe(currRef)

    window.addEventListener('scroll', throttledUpdate, { passive: true })
    window.addEventListener('resize', throttledUpdate)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('scroll', throttledUpdate)
      window.removeEventListener('resize', throttledUpdate)
    }
  }, [step])

  // disable scrolling when guided tour is open
  useEffect(() => {
    if (isGuidedTourOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      document.body.style.overflow = 'auto'
      document.body.style.paddingRight = '0px'
    }
  }, [isGuidedTourOpen])

  if (!isGuidedTourOpen || !step) {
    return null
  }

  return (
    <Box sx={{ pointerEvents: 'none' }}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
          zIndex: 1400,
          bgcolor: `${grey[200]}45`
        }}
      >
        <Fade in={true} timeout={{ enter: 500, exit: 500 }} key={step.key.split('-')[0]}>
          <Box>
            <HighlightBox
              height={rect?.left ? `${rect.height}px` : 'auto'}
              width={rect?.left ? `${rect.width}px` : 'auto'}
              left={rect?.left ? `${rect.left}px` : undefined}
              top={rect?.top ? `${rect?.top}px` : undefined}
              handleClose={handleClose}
              handleNextClicked={handleNextClicked}
              title={step.title}
              description={step.description}
              disableHighlight={step.disableHighlight}
              index={currStep}
              stepLength={steps.length - 1}
              textBoxXPlacement={step.textBoxXPlacement}
              textBoxYPlacement={step.textBoxYPlacement}
              handlePreviousClicked={handlePreviousClicked}
            />
          </Box>
        </Fade>
      </Box>
    </Box>
  )
}
